import type { ChangeEvent, ReactNode } from 'react'
import type { FileRejection } from 'react-dropzone'
import { useCallback, useEffect, useState } from 'react'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { Formik, type FormikProps } from 'formik'
import { useShallow } from 'zustand/react/shallow'
import {
  faAnchor,
  faBiking,
  faCar,
  faTractor,
  faTruck,
} from '@fortawesome/pro-regular-svg-icons'
import { InferType, mixed, object, string } from 'yup'

//import Button from '../../Button'
import Dialog from '../../Dialog'
import { getToolOptions } from '../../../utils/uploads'
import InputGroup from '../../InputGroup'
import RadioCard from '../../RadioCard'
import useAnalyseUpload from '../../../hooks/useAnalyseUpload'
import useAuth from '../../../hooks/useAuth'
import loadingAnimation from '../../../assets/lottie/autoresponder-2.lottie'
import { PrivateAPI } from '../../../utils/api'
import FileDropzone, {
  type TFileDropzoneFile,
  type TFileDropzoneInputFile,
} from '../../FileDropzone'
import NewUploadNegativeCreditsModal from './NegativeCreditsModal'
import NewUploadNoCreditsModal from './NoCreditsModal'
import NewUploadNoPaymentMethodsModal from './NoPaymentMethodsModal'
import TermsModal from '../../TermsModal'
import useNewUpload, {
  getStateFromAnalysedFile,
  type TNewUploadAnalysedFile,
  type TNewUploadAnalysedFileInput,
  type TNewUploadFile,
  type TNewUploadVehicle,
  type TVehicleType,
} from '../../../hooks/useNewUpload'

type Props = {
  formId: string
  onSubmitSuccess: (fileId: string) => void
}

type TContainerProps = {
  children: ReactNode
}

type TAnalyseFormProps = {
  formId: Props['formId']
  formik: FormikProps<TAnalyseValues>
  submitError?: string
}

type TRouteParams = {
  fileId?: string
}

type TAnalysedFileResponse = {
  analysedFile: TNewUploadAnalysedFile
}

type TPaymentMethodsResponse = {
  paymentMethods: unknown[]
}

type TAnalyseStatusResponse = {
  status: 'PROCESSING' | 'READY' | 'FAILED'
}

const MAX_FILE_SIZE = 1024 * 1024 * 25
const ANALYSE_POLL_TIMEOUT_MS = 1000 * 60 * 2

type TAnalyseLocationState = {
  shouldResumeAnalysis?: boolean
}

const analyseSchema = object({
  vehicleType: string<TVehicleType>()
    .oneOf(['CAR', 'TRUCK', 'MOTORBIKE', 'TRACTOR', 'BOAT'])
    .required('Select a vehicle type'),
  vehicle: object({
    make: string().trim().required('Enter the vehicle make'),
    model: string().trim().required('Enter the vehicle model'),
    engineSize: string().trim().required('Enter the engine size'),
    hpModel: string().trim().default(''),
    fuel: string<TNewUploadVehicle['fuel']>()
      .oneOf(['', 'PETROL', 'DIESEL', 'OTHER'])
      .test('fuel-required', 'Select the fuel type', (value) => {
        return value === 'PETROL' || value === 'DIESEL' || value === 'OTHER'
      })
      .required('Select the fuel type'),
    year: string().trim().required('Enter the vehicle year'),
  }).required(),
  vrm: string().trim().default(''),
  reference: string().trim().default(''),
  mileage: string().trim().required('Enter the vehicle mileage'),
  tune: object({
    ecuType: string().trim().required('Enter the ECU type'),
    tuningTool: string().trim().required('Select the tuning tool used'),
    tuningToolOther: string().when('tuningTool', {
      is: 'OTHER',
      then: (schema) => schema.trim().required('Enter the tuning tool used'),
      otherwise: (schema) => schema.trim().default(''),
    }),
  }).required(),
  file: mixed<TNewUploadFile>()
    .required('Upload an ECU file')
    .test('file-accepted', 'Upload a valid ECU file', (value) => {
      return Boolean(value?.isAccepted && !value.isRejected)
    }),
}).required()

type TAnalyseValues = InferType<typeof analyseSchema>

type TValidatedAnalyseValues = TAnalyseValues & {
  file: TNewUploadFile
  tune: {
    ecuType: string
    tuningTool: string
    tuningToolOther: string
  }
  vehicle: TNewUploadVehicle
}

const INITIAL_VALUES: TAnalyseValues = {
  vehicleType: 'CAR',
  vehicle: {
    make: '',
    model: '',
    engineSize: '',
    hpModel: '',
    fuel: '',
    year: '',
  },
  vrm: '',
  reference: '',
  mileage: '',
  tune: {
    ecuType: '',
    tuningTool: '',
    tuningToolOther: '',
  },
  file: null,
}

const VehicleTypeContainer = (props: TContainerProps) => {
  const { children } = props

  return (
    <div className="space-y-2 md:grid md:grid-cols-2 md:gap-2 md:space-y-0 lg:grid-cols-3">
      {children}
    </div>
  )
}

const formatMileage = (value: string) => {
  const digits = value.replace(/\D/g, '')

  if (!digits) {
    return ''
  }

  return parseInt(digits, 10).toLocaleString('en-GB')
}

const mapMainFile = (file: TFileDropzoneInputFile): TNewUploadFile => {
  const isTooLarge = file.size > MAX_FILE_SIZE

  const rejectReason = file.rejectReason
    ? file.rejectReason
    : isTooLarge
      ? 'File size is too large'
      : null

  return {
    id: null,
    name: file.name,
    size: file.size,
    fileBlob: file,
    isUploading: false,
    uploadProgress: null,
    isUploaded: false,
    isAccepted: !rejectReason,
    isRejected: Boolean(rejectReason),
    rejectReason,
    uploadErr: null,
    uploadUrl: null,
    isPreparing: null,
  }
}

const AnalyseForm = (props: TAnalyseFormProps) => {
  const { formId, formik, submitError } = props
  const isDisabled = formik.isSubmitting

  const handleFileAccepted = useCallback(
    (files: File[]) => {
      const file = files[0]

      if (!file) {
        return
      }

      formik.setFieldValue('file', mapMainFile(file), false)
      formik.setFieldError('file', undefined)
    },
    [formik],
  )

  const handleFileRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      const fileRejection = fileRejections[0]

      if (!fileRejection) {
        return
      }

      const inputFile = Object.assign(fileRejection.file, {
        rejectReason: fileRejection.errors[0]?.message,
      }) as TFileDropzoneInputFile

      formik.setFieldValue('file', mapMainFile(inputFile), false)
    },
    [formik],
  )

  const handleFileRemove = useCallback(() => {
    formik.setFieldValue('file', null, false)
  }, [formik])

  const handleMileageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const numericValue = formatMileage(event.target.value)

      formik.setFieldValue('mileage', numericValue, false)
    },
    [formik],
  )

  const fileError =
    formik.values.file?.rejectReason ||
    formik.values.file?.uploadErr?.message ||
    (typeof formik.errors.file === 'string' ? formik.errors.file : null) ||
    submitError

  const fileList: TFileDropzoneFile[] = formik.values.file
    ? [formik.values.file]
    : []

  return (
    <form id={formId} onSubmit={formik.handleSubmit}>
      <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-5">
        <div className="col-span-5 sm:col-span-3 md:col-span-2 border-r border-gray-200 bg-gray-50 p-6">
          <h3 className="block text-sm font-medium leading-5 text-black">
            File upload
          </h3>
          <p className="pb-4 text-sm text-gray-600">
            Upload the ECU file to be tuned.
          </p>
          <FileDropzone
            files={fileList}
            multiple={false}
            onAccepted={handleFileAccepted}
            onRejected={handleFileRejected}
            onRemoveFile={handleFileRemove}
            disabled={isDisabled}
          />
          {fileError && (
            <p className="mt-2 text-sm text-red-600">{fileError}</p>
          )}
        </div>
        <div className="col-span-5 sm:col-span-3 space-y-4 p-6">
          <div>
            <p className="mb-2 block text-sm font-medium leading-5 text-black">
              Vehicle type
            </p>
            <RadioCard
              label="Vehicle type"
              selected={formik.values.vehicleType}
              setSelected={(vehicleType: TVehicleType) => {
                if (isDisabled) {
                  return
                }

                formik.setFieldValue('vehicleType', vehicleType, false)
                formik.setFieldError('vehicleType', undefined)
              }}
              Container={VehicleTypeContainer}
              items={[
                {
                  label: 'Car',
                  icon: faCar,
                  value: 'CAR',
                },
                {
                  label: 'Truck',
                  icon: faTruck,
                  value: 'TRUCK',
                },
                {
                  label: 'Motorbike',
                  icon: faBiking,
                  value: 'MOTORBIKE',
                },
                {
                  label: 'Tractor',
                  icon: faTractor,
                  value: 'TRACTOR',
                },
                {
                  label: 'Boat',
                  icon: faAnchor,
                  value: 'BOAT',
                },
              ]}
            />
            {formik.errors.vehicleType && (
              <p className="mt-2 text-sm text-red-600">
                {formik.errors.vehicleType}
              </p>
            )}
          </div>
          <div className="space-y-4">
            <div className="space-y-4 md:flex md:flex-1 md:space-x-3 md:space-y-0">
              <InputGroup
                label="Make"
                id="vehicleMake"
                name="vehicle.make"
                required
                className="flex-1"
                placeholder="Enter vehicle make"
                disabled={isDisabled}
                onChange={formik.handleChange}
                value={formik.values.vehicle.make}
                error={formik.errors.vehicle?.make}
              />
              <InputGroup
                label="Model"
                id="vehicleModel"
                name="vehicle.model"
                required
                className="flex-1"
                placeholder="Enter vehicle model"
                disabled={isDisabled}
                onChange={formik.handleChange}
                value={formik.values.vehicle.model}
                error={formik.errors.vehicle?.model}
              />
            </div>
            <div className="space-y-4 md:flex md:flex-1 md:space-x-3 md:space-y-0">
              <InputGroup
                label="Engine size"
                id="vehicleEngineSize"
                name="vehicle.engineSize"
                required
                className="flex-1"
                placeholder="Enter engine size"
                disabled={isDisabled}
                onChange={formik.handleChange}
                value={formik.values.vehicle.engineSize}
                error={formik.errors.vehicle?.engineSize}
              />
              <InputGroup
                label="Fuel"
                id="fuelType"
                name="vehicle.fuel"
                required
                type="select"
                className="flex-1"
                disabled={isDisabled}
                onChange={formik.handleChange}
                value={formik.values.vehicle.fuel}
                error={formik.errors.vehicle?.fuel}
                renderOptions={() => (
                  <>
                    <option value="">Select fuel type</option>
                    <option value="PETROL">Petrol</option>
                    <option value="DIESEL">Diesel</option>
                    <option value="OTHER">Other</option>
                  </>
                )}
              />
            </div>
            <div className="space-y-4 md:flex md:flex-1 md:space-x-3 md:space-y-0">
              <InputGroup
                label="Year"
                id="vehicleYear"
                name="vehicle.year"
                required
                className="flex-1"
                placeholder="Enter vehicle year"
                disabled={isDisabled}
                onChange={formik.handleChange}
                value={formik.values.vehicle.year}
                error={formik.errors.vehicle?.year}
              />
              <InputGroup
                label="HP model"
                id="vehicleHpModel"
                name="vehicle.hpModel"
                className="flex-1"
                placeholder="Enter vehicle HP model"
                disabled={isDisabled}
                onChange={formik.handleChange}
                value={formik.values.vehicle.hpModel}
                error={formik.errors.vehicle?.hpModel}
              />
            </div>
            <div className="space-y-4 md:flex md:flex-1 md:space-x-3 md:space-y-0">
              <InputGroup
                label="Vehicle mileage"
                id="mileage"
                name="mileage"
                required
                className="flex-1"
                inputMode="numeric"
                placeholder="Enter mileage"
                disabled={isDisabled}
                onChange={handleMileageChange}
                value={formik.values.mileage}
                error={formik.errors.mileage}
              />
              <InputGroup
                label="ECU type"
                id="ecuType"
                name="tune.ecuType"
                required
                className="flex-1"
                placeholder="Enter ECU type"
                disabled={isDisabled}
                onChange={formik.handleChange}
                value={formik.values.tune.ecuType}
                error={formik.errors.tune?.ecuType}
              />
            </div>
            <div className="space-y-4 md:flex md:flex-1 md:space-x-3 md:space-y-0">
              <InputGroup
                label="Tuning tool used"
                id="tuningTool"
                name="tune.tuningTool"
                type="select"
                className="flex-1"
                required
                disabled={isDisabled}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  formik.handleChange(e)

                  if (e.target.value !== 'OTHER') {
                    formik.setFieldValue('tune.tuningToolOther', '', false)
                  }
                }}
                value={formik.values.tune.tuningTool}
                error={formik.errors.tune?.tuningTool}
                renderOptions={() => (
                  <>
                    <option value="">Select tuning tool</option>
                    {getToolOptions()}
                  </>
                )}
              />
              {formik.values.tune.tuningTool === 'OTHER' ? (
                <InputGroup
                  label="Other tuning tool used"
                  id="tuningToolOther"
                  name="tune.tuningToolOther"
                  required
                  className="flex-1"
                  placeholder="Enter tuning tool used"
                disabled={isDisabled}
                  onChange={formik.handleChange}
                  value={formik.values.tune.tuningToolOther}
                  error={formik.errors.tune?.tuningToolOther}
                />
              ) : (
                <InputGroup
                  label="Reference (optional)"
                  id="reference"
                  name="reference"
                  className="flex-1"
                  placeholder="VRM, VIN etc."
                  disabled={isDisabled}
                  onChange={formik.handleChange}
                  help="Searchable reference for your upload"
                  value={formik.values.reference}
                />
              )}
            </div>
            {formik.values.tune.tuningTool === 'OTHER' && (
              <div className="w-1/2">
                <InputGroup
                  label="Reference (optional)"
                  id="reference"
                  name="reference"
                  className="flex-1"
                  placeholder="VRM, VIN etc."
                  disabled={isDisabled}
                  onChange={formik.handleChange}
                  help="Searchable reference for your upload"
                  value={formik.values.reference}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </form>
  )
}

const Analyse = (props: Props) => {
  const { formId, onSubmitSuccess } = props
  const [auth] = useAuth()
  const analyseUploadMutation = useAnalyseUpload()
  const {
    analysedFile,
    file,
    isNexusAvailable,
    mileage,
    reference,
    setState,
    tune,
    vehicle,
    vehicleType,
    vrm,
  } = useNewUpload(
    useShallow((state) => ({
      analysedFile: state.analysedFile,
      file: state.file,
      isNexusAvailable: state.isNexusAvailable,
      mileage: state.mileage,
      reference: state.reference,
      setState: state.setState,
      tune: state.tune,
      vehicle: state.vehicle,
      vehicleType: state.vehicleType,
      vrm: state.vrm,
    })),
  )
  const queryClient = useQueryClient()
  const history = useHistory()
  const location = useLocation<TAnalyseLocationState>()
  const { fileId } = useParams<TRouteParams>()
  const pollingAnalysedFileId = analysedFile?.id
  const [analysisError, setAnalysisError] = useState('')
  const [isAnalysing, setIsAnalysing] = useState(false)
  const [isAwaitingAnalysis, setIsAwaitingAnalysis] = useState(false)
  const [analysisStartedAt, setAnalysisStartedAt] = useState<number | null>(
    null,
  )
  const [termsModalOpen, setTermsModalOpen] = useState(false)
  const [negativeCreditsModalOpen, setNegativeCreditsModalOpen] =
    useState(false)
  const [noCreditsModalOpen, setNoCreditsModalOpen] = useState(false)
  const [noPaymentMethodsModalOpen, setNoPaymentMethodsModalOpen] =
    useState(false)
  const missingAnalysedFile =
    Boolean(fileId) && analysedFile?.id !== fileId
  const shouldResumeAnalysis = location.state?.shouldResumeAnalysis !== false
  const creditBalance = auth.membership.dealer.credit?.balance || 0
  const isMasterDealer = auth.membership.dealer.type === 'MASTER'

  const paymentMethodsQuery = useQuery({
    queryKey: ['billing-payment-methods'],
    queryFn: async () => {
      const response = await PrivateAPI<TPaymentMethodsResponse>({
        url: '/billing/payment-methods',
        method: 'get',
      })

      return response.data.paymentMethods
    },
  })

  const analysedFileQuery = useQuery({
    queryKey: ['analysedFile', fileId],
    queryFn: async () => {
      const response = await PrivateAPI<TAnalysedFileResponse>({
        url: `/uploads/analyse/${fileId}`,
        method: 'get',
      })

      return response.data.analysedFile
    },
    enabled: missingAnalysedFile,
  })

  const analyseStatusQuery = useQuery({
    queryKey: ['analysedFile-status', pollingAnalysedFileId],
    queryFn: async () => {
      const response = await PrivateAPI<TAnalyseStatusResponse>({
        url: `/uploads/analyse/${pollingAnalysedFileId}/status`,
        method: 'get',
      })

      return response.data.status
    },
    enabled: isAwaitingAnalysis && Boolean(pollingAnalysedFileId),
    refetchInterval: (query) => {
      if (
        analysisStartedAt &&
        Date.now() - analysisStartedAt >= ANALYSE_POLL_TIMEOUT_MS
      ) {
        return false
      }

      return query.state.data === 'PROCESSING' || query.state.data === undefined
        ? 5000
        : false
    },
  })

  useEffect(() => {
    if (auth.membership.dealer.terms.requiresAcceptance) {
      setTermsModalOpen(true)
      return
    }

    if (creditBalance < 0) {
      setNegativeCreditsModalOpen(true)
      return
    }

    if (!isMasterDealer && creditBalance < 0.25) {
      setNoCreditsModalOpen(true)
      return
    }

    if (paymentMethodsQuery.isSuccess && paymentMethodsQuery.data.length < 1) {
      setNoPaymentMethodsModalOpen(true)
      return
    }

    setTermsModalOpen(false)
    setNegativeCreditsModalOpen(false)
    setNoCreditsModalOpen(false)
    setNoPaymentMethodsModalOpen(false)
  }, [
    auth.membership.dealer.terms.requiresAcceptance,
    creditBalance,
    isMasterDealer,
    paymentMethodsQuery.data,
    paymentMethodsQuery.isSuccess,
  ])

  useEffect(() => {
    if (!analysedFileQuery.data) {
      return
    }

    if (analysedFile?.id === analysedFileQuery.data.id) {
      return
    }

    setState(getStateFromAnalysedFile(analysedFileQuery.data))
  }, [analysedFile?.id, analysedFileQuery.data, setState])

  useEffect(() => {
    if (!fileId || analysedFile?.id !== fileId) {
      setIsAnalysing(false)
      setIsAwaitingAnalysis(false)
      setAnalysisStartedAt(null)
      return
    }

    if (
      analysedFile.processingStatus !== 'PROCESSING' || !shouldResumeAnalysis
    ) {
      setIsAnalysing(false)
      setIsAwaitingAnalysis(false)
      setAnalysisStartedAt(null)
      return
    }

    const startedAt = Date.parse(analysedFile.createdAt)

    void queryClient.removeQueries({
      queryKey: ['analysedFile-status', fileId],
      exact: true,
    })
    setIsAnalysing(true)
    setAnalysisStartedAt(Number.isNaN(startedAt) ? Date.now() : startedAt)
    setIsAwaitingAnalysis(true)
  }, [
    analysedFile?.createdAt,
    analysedFile?.id,
    analysedFile?.processingStatus,
    fileId,
    queryClient,
    shouldResumeAnalysis,
  ])

  const finalizeAnalysis = useCallback(
    async (analysedFileId: string) => {
      try {
        const response = await PrivateAPI<TAnalysedFileResponse>({
          url: `/uploads/analyse/${analysedFileId}`,
          method: 'get',
        })
        const analysedFile = response.data.analysedFile

        setIsAnalysing(false)
        setIsAwaitingAnalysis(false)
        setAnalysisStartedAt(null)
        setState(getStateFromAnalysedFile(analysedFile))
        setState({
          analyse: {
            isSubmitting: false,
          },
        })

        onSubmitSuccess(analysedFile.id)
      } catch {
        setIsAnalysing(false)
        setIsAwaitingAnalysis(false)
        setAnalysisStartedAt(null)
        setAnalysisError('An unexpected error occured')
        setState({
          analyse: {
            isSubmitting: false,
          },
        })
      }
    },
    [onSubmitSuccess, setState],
  )

  useEffect(() => {
    if (!isAwaitingAnalysis) {
      return
    }

    if (
      (analyseStatusQuery.data !== 'READY' &&
        analyseStatusQuery.data !== 'FAILED') ||
      !pollingAnalysedFileId
    ) {
      return
    }

    void finalizeAnalysis(pollingAnalysedFileId)
  }, [
    analyseStatusQuery.data,
    finalizeAnalysis,
    isAwaitingAnalysis,
    pollingAnalysedFileId,
  ])

  useEffect(() => {
    if (!isAwaitingAnalysis || !analysisStartedAt || !pollingAnalysedFileId) {
      return
    }

    const timeoutMs = ANALYSE_POLL_TIMEOUT_MS - (Date.now() - analysisStartedAt)

    if (timeoutMs <= 0) {
      void finalizeAnalysis(pollingAnalysedFileId)
      return
    }

    const timeoutId = window.setTimeout(() => {
      void finalizeAnalysis(pollingAnalysedFileId)
    }, timeoutMs)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    analysisStartedAt,
    finalizeAnalysis,
    isAwaitingAnalysis,
    pollingAnalysedFileId,
  ])

  const handleSubmit = useCallback(
    async (values: TAnalyseValues) => {
      const validatedValues = values as TValidatedAnalyseValues

      const input: TNewUploadAnalysedFileInput = {
        vehicleType: validatedValues.vehicleType,
        vehicle: validatedValues.vehicle,
        vrm: validatedValues.vrm,
        reference: validatedValues.reference,
        mileage: validatedValues.mileage.replace(/,/g, ''),
        tune: validatedValues.tune,
      }

      setAnalysisError('')
      setIsAnalysing(true)
      setState({
        analyse: {
          isSubmitting: true,
        },
      })

      try {
        const analyseUpload = await analyseUploadMutation.mutateAsync({
          analysedFileId: fileId,
          file: validatedValues.file,
          input,
        })

        const nextState = getStateFromAnalysedFile(analyseUpload.analysedFile)

        setState(nextState)

        if (analyseUpload.analysedFile.processingStatus !== 'PROCESSING') {
          setState({
            analyse: {
              isSubmitting: false,
            },
          })
          onSubmitSuccess(analyseUpload.analysedFile.id)
          return
        }

        await queryClient.removeQueries({
          queryKey: ['analysedFile-status', analyseUpload.analysedFile.id],
          exact: true,
        })

        history.replace({
          pathname: `/uploads/new/${analyseUpload.analysedFile.id}`,
          state: {
            shouldResumeAnalysis: true,
          },
        })
        setAnalysisStartedAt(
          Date.parse(analyseUpload.analysedFile.createdAt) || Date.now(),
        )
        setIsAwaitingAnalysis(true)
      } catch (err) {
        setIsAnalysing(false)
        setState({
          analyse: {
            isSubmitting: false,
          },
        })
        throw err
      }
    },
    [
      analyseUploadMutation,
      fileId,
      history,
      onSubmitSuccess,
      queryClient,
      setState,
    ],
  )

  const initialValues = {
    ...INITIAL_VALUES,
    vehicleType,
    vehicle: {
      ...INITIAL_VALUES.vehicle,
      ...vehicle,
    },
    vrm: vrm || INITIAL_VALUES.vrm,
    reference: reference || INITIAL_VALUES.reference,
    mileage: formatMileage(mileage || INITIAL_VALUES.mileage),
    tune: {
      ...INITIAL_VALUES.tune,
      ...tune,
    },
    file: file || INITIAL_VALUES.file,
  }

  return (
    <>
      <Dialog
        isOpen={
          isNexusAvailable &&
          (isAnalysing ||
            (isAwaitingAnalysis &&
              (analyseStatusQuery.isLoading ||
                analyseStatusQuery.data === 'PROCESSING')))
        }
        title=""
        buttonProps={null}
        panelClassName="max-w-sm"
      >
        <div className="flex flex-col items-center justify-center py-4 text-center">
          <DotLottieReact
            src={loadingAnimation}
            autoplay
            loop
            className="h-60 w-60"
            speed={1.5}
          />
          <h3 className="mt-2 text-lg font-semibold text-gray-900">
            Topgear Nexus working...
          </h3>
          <p className="mt-2 max-w-sm text-sm text-gray-600">
            Detecting available tune options
          </p>
        </div>
      </Dialog>
      <NewUploadNoCreditsModal isOpen={noCreditsModalOpen} />
      <NewUploadNegativeCreditsModal isOpen={negativeCreditsModalOpen} />
      <NewUploadNoPaymentMethodsModal isOpen={noPaymentMethodsModalOpen} />
      <TermsModal
        isOpen={termsModalOpen}
        onAccept={() => setTermsModalOpen(false)}
        setIsOpen={() => history.push('/uploads')}
      />
      <Formik<TAnalyseValues>
        initialValues={initialValues}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={analyseSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <AnalyseForm
            formId={formId}
            formik={formik}
            submitError={analyseUploadMutation.error?.message || analysisError}
          />
        )}
      </Formik>
    </>
  )
}

export default Analyse

/** 
 * 
 * {formik.values.vehicleType === 'CAR' && (
              <div>
                <h4 className="text-sm font-medium leading-6 text-black">
                  VRM lookup
                </h4>
                <p className="mb-2 text-xs text-gray-700">
                  UK only and optional
                </p>
                <div className="sm:flex space-x-2">
                  <InputGroup
                    className="mb-4 sm:mb-0 sm:max-w-xs sm:flex-1"
                    labelHidden
                    label="UK car reg"
                    name="vrm"
                    placeholder="Enter UK car registration"
                    id="reg"
                  disabled={isDisabled}
                    onChange={formik.handleChange}
                    value={formik.values.vrm}
                  />
                  <Button onClick={() => {}} color="primary" disabled={false}>
                    Autofill
                  </Button>
                </div>
              </div>
            )}
*/
